/**
 * Navigation helper for opening search results
 *
 * Uses URL query params to trigger existing dialog hooks.
 * This ensures deep-linking works and URLs are shareable.
 */

import type { GlobalSearchResult } from './SearchProvider';

/**
 * Build the URL for opening a search result
 *
 * @param result - The search result to open
 * @returns URL path with query params using canonical dialog format
 *
 * @example
 * buildSearchResultUrl(vehicleResult)
 * // Returns: '/vehicles?dialog=vehicle&mode=view&id=v-001'
 */
export function buildSearchResultUrl(result: GlobalSearchResult): string {
  const { route, dialog, idParam } = result.openTarget;
  return `${route}?dialog=${encodeURIComponent(dialog)}&mode=view&id=${encodeURIComponent(idParam)}`;
}

/**
 * Open a search result by navigating to its URL
 *
 * This function is designed to be called with a React Router navigate function.
 * It builds the appropriate URL with query params that existing dialog hooks
 * will pick up to open the correct view/edit drawer.
 *
 * @param result - The search result to open
 * @param navigate - React Router navigate function
 *
 * @example
 * const navigate = useNavigate();
 * openSearchResult(result, navigate);
 */
export function openSearchResult(
  result: GlobalSearchResult,
  navigate: (path: string) => void
): void {
  const url = buildSearchResultUrl(result);
  navigate(url);
}
